import { Component } from "@theme/component";

class ProductRecommendations extends Component {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: "0px 0px 400px 0px" },
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== "attributes") continue;

      // Ignore error attribute changes
      if (mutation.attributeName === "data-error") continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (
        mutation.attributeName === "class" &&
        this.classList.contains("hidden")
      )
        continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === "data-recommendations-performed" &&
        this.dataset.recommendationsPerformed === "true"
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } =
      this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error("Product ID and an ID attribute are required");
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === "true") {
      return;
    }

    if (intent === "product_type") {
      this.#loadByProductType();
      return;
    }

    if (intent === "sku_prefix") {
      this.#loadBySkuPrefix();
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement("div");
        html.innerHTML = result.data || "";
        const recommendations = html.querySelector(
          `product-recommendations[id="${id}"]`,
        );

        if (
          recommendations?.innerHTML &&
          recommendations.innerHTML.trim().length
        ) {
          this.dataset.recommendationsPerformed = "true";
          this.innerHTML = recommendations.innerHTML;
        } else {
          this.#handleError(new Error("No recommendations available"));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Shuffle array using Fisher-Yates algorithm
   */
  #shuffleArray(array) {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  }

  /**
   * Load products by product type via search page
   */
  #loadByProductType() {
    const { productType } = this.dataset;

    if (!productType) {
      this.#handleError(new Error("Product type is required"));
      return;
    }

    const searchUrl = `/search?type=product&q=product_type:${encodeURIComponent(`"${productType}"`)}&limit=250`;
    this.#loadBySearch(searchUrl);
  }

  /**
   * Load products by first variant SKU prefix via search page.
   * Falls back to Shopify related recommendations if SKU doesn't match known prefixes.
   */
  #loadBySkuPrefix() {
    const { firstVariantSku, productId, sectionId } = this.dataset;

    // Check if SKU starts with a known seller prefix (e.g. TS00-10062402-1 → starts with "TS")
    const knownPrefixes = ["LT", "CTM", "DT", "TS", "K23"];
    const hasKnownPrefix =
      typeof firstVariantSku === "string" &&
      knownPrefixes.some((p) => firstVariantSku.toUpperCase().startsWith(p));

    if (!hasKnownPrefix) {
      // Fallback to Shopify related recommendations
      this.#fetchCachedRecommendations(productId, sectionId, "related")
        .then((result) => {
          if (!result.success) {
            if (!Shopify.designMode) {
              this.#handleError(new Error(`Server returned ${result.status}`));
            }
            return;
          }

          const html = document.createElement("div");
          html.innerHTML = result.data || "";
          const recommendations = html.querySelector(
            `product-recommendations[id="${this.id}"]`,
          );

          if (
            recommendations?.innerHTML &&
            recommendations.innerHTML.trim().length
          ) {
            this.dataset.recommendationsPerformed = "true";
            this.innerHTML = recommendations.innerHTML;
          } else {
            this.#handleError(new Error("No recommendations available"));
          }
        })
        .catch((e) => {
          this.#handleError(e);
        });
      return;
    }

    // Extract SKU base: 4-char prefix + dash + 8-digit code (e.g. TS00-10062402-1 → TS00-10062402)
    const [, skuBase] = firstVariantSku.match(/^([A-Z0-9]{4}-\d{8})/) || [];
    if (!skuBase) {
      this.#handleError(new Error("Invalid SKU format"));
      return;
    }
    const searchUrl = `/search?type=product&q=${encodeURIComponent(skuBase)}&limit=250`;
    this.#loadBySearch(searchUrl);
  }

  /**
   * Shared method: fetch search results, filter, shuffle, and render into grid/carousel
   */
  async #loadBySearch(searchUrl) {
    const { productId, maxProducts, layoutType } = this.dataset;

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(searchUrl, {
        signal: this.#activeFetch.signal,
      });
      if (!response.ok) {
        this.#handleError(new Error(`Server returned ${response.status}`));
        return;
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");

      const items = Array.from(doc.querySelectorAll(".product-grid__item"));
      const filtered = items.filter(
        (item) => item.getAttribute("data-product-id") !== productId,
      );

      if (filtered.length === 0) {
        this.#handleError(new Error("No related products found"));
        return;
      }

      const max = parseInt(maxProducts, 10) || 8;
      const selected = this.#shuffleArray(filtered).slice(0, max);

      const grid = this.querySelector(".resource-list");
      if (!grid) return;

      if (layoutType === "carousel") {
        const slidesHtml = selected
          .map(
            (item, i) => `
          <slideshow-slide
            ref="slides[]"
            aria-hidden="${i === 0 ? "false" : "true"}"
            ${i !== 0 ? "inert" : ""}
            style="--slideshow-timeline: --slide-${i};"
            class="resource-list__slide"
          >${item.innerHTML}</slideshow-slide>
        `,
          )
          .join("");

        const timelineScope = selected.map((_, i) => `--slide-${i}`).join(",");

        const arrowSvg =
          '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="var(--icon-stroke-width)" vector-effect="non-scaling-stroke" d="M4.25 10h11.5m0 0-4-4m4 4-4 4"/></svg>';

        const arrowsHtml = `
          <slideshow-arrows position="center">
            <button class="slideshow-control slideshow-control--previous slideshow-control--style-arrow button button-unstyled button-unstyled--transparent flip-x" aria-label="Previous" on:click="/previous" ref="previous">
              <span class="svg-wrapper icon-arrow">${arrowSvg}</span>
            </button>
            <button class="slideshow-control slideshow-control--next slideshow-control--style-arrow button button-unstyled button-unstyled--transparent" aria-label="Next" on:click="/next" ref="next">
              <span class="svg-wrapper icon-arrow">${arrowSvg}</span>
            </button>
          </slideshow-arrows>
        `;

        grid.className =
          "resource-list resource-list__carousel force-full-width";
        grid.innerHTML = `
          <div class="resource-list__carousel" style="--gutter-slide-width: var(--util-page-margin-offset); --slide-width-max: 300px;">
            <slideshow-component
              class="resource-list__carousel"
              style="--slideshow-timeline: ${timelineScope};"
              initial-slide="0"
            >
              <slideshow-container ref="slideshowContainer">
                ${arrowsHtml}
                <slideshow-slides tabindex="-1" ref="scroller" gutters="start end">
                  ${slidesHtml}
                </slideshow-slides>
              </slideshow-container>
            </slideshow-component>
          </div>
        `;
      } else {
        grid.innerHTML = "";
        grid.className = "resource-list resource-list--grid";
        selected.forEach((item) => {
          const wrapper = document.createElement("div");
          wrapper.className = "resource-list__item";
          wrapper.innerHTML = item.innerHTML;
          grid.appendChild(wrapper);
        });
      }

      grid.setAttribute("data-has-recommendations", "true");
      this.dataset.recommendationsPerformed = "true";
    } catch (e) {
      if (e.name !== "AbortError") {
        this.#handleError(e);
      }
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error("Product recommendations error:", error.message);
    this.classList.add("hidden");
    this.dataset.error = "Error loading product recommendations";
  }
}

if (!customElements.get("product-recommendations")) {
  customElements.define("product-recommendations", ProductRecommendations);
}
